import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ArrowLeft, Save, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useBusinessAuth } from '@/context/BusinessAuthContext';

const API_URL = '';

const CATEGORIES = [
  { value: 'eat', label: 'Eat' },
  { value: 'do', label: 'Do' },
  { value: 'explore', label: 'Explore' },
  { value: 'experiences', label: 'Experiences' },
  { value: 'carhire', label: 'Car Hire' },
  { value: 'wellness', label: 'Wellness' },
  { value: 'nightlife', label: 'Nightlife' },
  { value: 'shopping', label: 'Shopping' },
];

export default function PortalListingPage() {
  const navigate = useNavigate();
  const { user, loading: authLoading, subscription, refreshUser } = useBusinessAuth();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: 'eat',
    phone: '',
    website: '',
    address: '',
    latitude: '',
    longitude: ''
  });

  const tier = subscription?.tier || 'starter';

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/partner/login');
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (user) {
      fetchBusiness();
    }
  }, [user]);

  const fetchBusiness = async () => {
    try {
      const response = await fetch(`${API_URL}/api/partner/portal/business`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setFormData({
          name: data.name || '',
          description: data.description || '',
          category: data.category || 'eat',
          phone: data.phone || '',
          website: data.website || '',
          address: data.address || '',
          latitude: data.latitude || '',
          longitude: data.longitude || ''
        });
      }
    } catch (error) {
      console.error('Failed to fetch business:', error);
      toast.error('Failed to load business data');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const response = await fetch(`${API_URL}/api/partner/portal/business`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success('Listing updated successfully');
        await refreshUser();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update listing');
      }
    } catch (error) {
      toast.error('Failed to update listing');
    } finally {
      setSaving(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-teal-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="portal-listing-page">
      {/* Header */}
      <header className="bg-white border-b border-slate-100 px-6 py-4">
        <div className="max-w-2xl mx-auto flex items-center gap-4">
          <Link 
            to="/portal"
            className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center hover:bg-slate-200 transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-slate-700" />
          </Link>
          <div>
            <h1 className="text-xl font-bold text-slate-900 font-heading">
              Manage Listing
            </h1>
            <p className="text-sm text-slate-500 capitalize">{tier} Plan</p>
          </div>
        </div>
      </header>

      {/* Form */}
      <main className="px-6 py-8">
        <form onSubmit={handleSubmit} className="max-w-2xl mx-auto space-y-6">
          {/* Basic Info */}
          <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm space-y-4">
            <h2 className="text-lg font-semibold text-slate-900 font-heading">Basic Information</h2>
            
            <div className="space-y-2">
              <Label htmlFor="name">Business Name</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder="Your business name"
                className="h-12"
                data-testid="input-name"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                placeholder="Describe your business..."
                rows={3}
                data-testid="input-description"
              />
            </div>

            <div className="space-y-2">
              <Label>Category</Label>
              <Select value={formData.category} onValueChange={(v) => handleChange('category', v)}>
                <SelectTrigger className="h-12" data-testid="select-category">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map(cat => (
                    <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Contact Info */}
          <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm space-y-4">
            <h2 className="text-lg font-semibold text-slate-900 font-heading">Contact Information</h2>
            
            <div className="space-y-2">
              <Label htmlFor="phone">Phone Number</Label>
              <Input
                id="phone"
                value={formData.phone}
                onChange={(e) => handleChange('phone', e.target.value)}
                placeholder="+61 7 1234 5678"
                className="h-12"
                data-testid="input-phone"
              />
            </div>

            {tier !== 'starter' ? (
              <div className="space-y-2">
                <Label htmlFor="website">Website URL</Label>
                <Input
                  id="website"
                  value={formData.website}
                  onChange={(e) => handleChange('website', e.target.value)}
                  placeholder="https://yourbusiness.com"
                  className="h-12"
                  data-testid="input-website"
                />
              </div>
            ) : (
              <div className="p-4 bg-slate-50 rounded-xl">
                <p className="text-sm text-slate-600">
                  <strong>Upgrade to Premium</strong> to add your website link.
                </p>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="address">Address</Label>
              <Textarea
                id="address"
                value={formData.address}
                onChange={(e) => handleChange('address', e.target.value)}
                placeholder="123 Main Street, Surfers Paradise QLD 4217"
                rows={2}
                data-testid="input-address"
              />
            </div>

            {/* Map Coordinates */}
            <div className="pt-4 border-t border-slate-100">
              <h3 className="text-sm font-medium text-slate-700 mb-2">Map Pin Location</h3>
              <p className="text-xs text-slate-500 mb-3">
                Ensure your business appears at the correct location on the map.
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="latitude">Latitude</Label>
                  <Input
                    id="latitude"
                    type="number"
                    step="any"
                    value={formData.latitude}
                    onChange={(e) => handleChange('latitude', e.target.value ? parseFloat(e.target.value) : "")}
                    placeholder="-28.0167"
                    className="h-12"
                    data-testid="input-latitude"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="longitude">Longitude</Label>
                  <Input
                    id="longitude"
                    type="number"
                    step="any"
                    value={formData.longitude}
                    onChange={(e) => handleChange('longitude', e.target.value ? parseFloat(e.target.value) : "")}
                    placeholder="153.4000"
                    className="h-12"
                    data-testid="input-longitude"
                  />
                </div>
              </div>
              <p className="text-xs text-slate-400 mt-2">
                Find your coordinates: Google Maps → Right-click your location → "What's here?"
              </p>
            </div>
          </div>

          {/* Submit */}
          <Button
            type="submit"
            disabled={saving}
            className="w-full h-12 rounded-full bg-teal-600 hover:bg-teal-700"
            data-testid="save-listing-btn"
          >
            {saving ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Save Changes
              </>
            )}
          </Button>
        </form>
      </main>
    </div>
  );
}
